import React from 'react';
import { Link } from 'react-router-dom';

const HeroBanner: React.FC = () => {
  return (
    <section
      className="relative min-h-[600px] flex items-center overflow-hidden bg-brand-navy"
      style={{ backgroundImage: "url('/images/hero/Hero.webp')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/85 via-brand-navy/70 to-brand-navy/80 z-[1]" aria-hidden="true" />

      <div className="relative z-[2] max-w-[1280px] mx-auto px-5 sm:px-10 py-20 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left — Content */}
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green-light text-xs font-semibold px-3.5 py-1.5 rounded-full uppercase tracking-wider mb-5">
            Canada's Multicultural Wholesale Partner
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-[56px] font-black leading-[1.08] text-white mb-5">
            Your Wholesale Partner for{' '}
            <em className="not-italic text-brand-teal-light relative">
              Every
              <span className="absolute bottom-0.5 left-0 w-full h-1 bg-brand-gold/60 rounded" />
            </em>{' '}
            Community
          </h1>

          <p className="text-[17px] leading-relaxed text-white/70 mb-8 max-w-[480px] mx-auto lg:mx-0">
            The wholesale source trusted by African, Caribbean, Asian, and Latin American stores across Canada — from tools and flags to incense and natural products. Built for shops that serve every community.
          </p>

          <div className="flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start">
            <Link
              to="/shop"
              className="bg-brand-teal text-white px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-brand-teal-dark transition-all shadow-lg shadow-brand-teal/25 hover:-translate-y-0.5"
            >
              Browse Products &rarr;
            </Link>
            <Link
              to="/apply"
              className="bg-gradient-to-br from-brand-gold to-yellow-600 text-brand-dark px-8 py-3.5 font-bold text-[15px] rounded-[10px] hover:-translate-y-0.5 transition-all shadow-lg shadow-brand-gold/30"
            >
              Apply for Wholesale
            </Link>
          </div>

          <dl className="flex gap-9 mt-10 pt-8 border-t border-white/10 justify-center lg:justify-start">
            <div>
              <dt className="font-display font-extrabold text-[32px] text-brand-teal-light leading-none">200+</dt>
              <dd className="text-[13px] text-white/50 mt-1">Products</dd>
            </div>
            <div>
              <dt className="font-display font-extrabold text-[32px] text-brand-teal-light leading-none">GTA</dt>
              <dd className="text-[13px] text-white/50 mt-1">Based &amp; Ships Canada-wide</dd>
            </div>
            <div>
              <dt className="font-display font-extrabold text-[32px] text-brand-teal-light leading-none">B2B</dt>
              <dd className="text-[13px] text-white/50 mt-1">Wholesale Only</dd>
            </div>
          </dl>
        </div>

        {/* Right — Quick Order Card */}
        <div className="hidden lg:flex justify-center relative" aria-hidden="true">
          <div className="w-[320px] bg-white/[0.07] backdrop-blur-xl border border-white/[0.12] rounded-[20px] p-7 text-white">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-gradient-to-br from-brand-teal to-brand-teal-dark rounded-xl flex items-center justify-center text-xl">
                <span role="img" aria-hidden="true">📦</span>
              </div>
              <div>
                <div className="font-bold text-base">Quick Order</div>
                <div className="text-xs text-white/50">Popular wholesale items</div>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { emoji: '🔪', name: 'Machetes', cat: 'Blades & Tools', unit: 'per case of 6' },
                { emoji: '🏴', name: 'Flag Bandanas', cat: 'Country Flags', unit: 'per dozen' },
                { emoji: '🧢', name: 'Caps & Hats', cat: 'Flag Headwear', unit: 'per dozen' },
              ].map((item) => (
                <div key={item.name} className="flex items-center justify-between bg-white/5 rounded-[10px] px-3.5 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[22px]">{item.emoji}</span>
                    <div>
                      <div className="font-semibold text-[13.5px]">{item.name}</div>
                      <div className="text-[11px] text-white/45">{item.cat}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm text-brand-teal-light">$XX.XX</div>
                    <span className="text-[11px] text-white/40">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating badges */}
          <div className="absolute -top-2.5 -right-8 bg-white rounded-[14px] px-4 py-3.5 shadow-xl flex items-center gap-2.5 animate-bounce" style={{ animationDuration: '3s' }}>
            <div className="w-9 h-9 bg-brand-green/10 rounded-lg flex items-center justify-center text-base">✓</div>
            <div>
              <div className="font-bold text-[13px] text-brand-navy">Canada-Wide</div>
              <div className="text-[11px] text-brand-text-muted">Fast shipping</div>
            </div>
          </div>
          <div className="absolute -bottom-2.5 -left-8 bg-white rounded-[14px] px-4 py-3.5 shadow-xl flex items-center gap-2.5 animate-bounce" style={{ animationDuration: '3s', animationDelay: '1.5s' }}>
            <div className="w-9 h-9 bg-brand-gold/15 rounded-lg flex items-center justify-center text-base">💳</div>
            <div>
              <div className="font-bold text-[13px] text-brand-navy">Interac e-Transfer</div>
              <div className="text-[11px] text-brand-text-muted">Easy payment</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
